import { useEffect, useMemo, useState } from 'react'
import Display from './components/Display'
import ErrorFallback from './components/ErrorFallback'
import ParamError from './components/ParamError'
import { TranslationProvider } from './context/TranslationContext'
import { demoScene, isDemoRequested } from './lib/demoFlag'
import { isE2eRequested } from './lib/e2eFlag'
import type { MessageSource } from './lib/messageSource'
import { createRtdbSource } from './lib/messageSource'
import { createDeviceRequestSource } from './lib/device-requests/source'
import { isParamError, parseDisplayParams } from './lib/params'
import { withFirebaseBranding } from './lib/placeBranding'
import { reportReceiverEvent } from './lib/sentry'
import { displaySession, reloadOnNewGrant } from './lib/receiverGrant'

export default function App() {
  const params = useMemo(() => parseDisplayParams(window.location.search), [])

  if (isParamError(params)) return <ParamError kind={params.error} />

  return (
    <TranslationProvider lang={params.lang}>
      <ConnectedDisplay placeId={params.placeId} userId={params.userId} />
    </TranslationProvider>
  )
}

function ConnectedDisplay({ placeId, userId }: { placeId: string; userId: string }) {
  const session = displaySession()
  useEffect(() => reloadOnNewGrant(), [])
  const [scriptedSource, setScriptedSource] = useState<MessageSource | null>(null)

  // Both scripted sources are dev/test-only. `import.meta.env.MODE` is
  // statically replaced, so production folds this to null and the dynamic
  // imports below become unreachable — the bundler drops them entirely.
  const scripted =
    import.meta.env.MODE === 'production'
      ? null
      : isDemoRequested(window.location.search)
        ? 'demo'
        : isE2eRequested(window.location.search)
          ? 'e2e'
          : null

  useEffect(() => {
    if (!scripted) return
    let current = true

    const load =
      scripted === 'demo'
        ? import('./demo/demoSource').then(({ createDemoSource }) =>
            createDemoSource(undefined, demoScene(window.location.search) === 'offline'),
          )
        : import('./e2e-support/windowSource').then(({ createWindowSource }) => createWindowSource())

    void load.then((source) => {
      if (current) setScriptedSource(withFirebaseBranding(source, placeId))
    })

    return () => {
      current = false
    }
  }, [scripted, placeId])

  // A session is either Reactivate's (receiver grant, socket) or a legacy
  // till's (Firebase) for its whole life, so one never overwrites the other.
  // Each brings its own branding: the socket's comes with its ready frame.
  const liveSource = useMemo(() => {
    if (scripted) return null
    if (session.kind === 'legacy') return withFirebaseBranding(createRtdbSource(placeId, userId), placeId)
    return createDeviceRequestSource({
      apiUrl: import.meta.env.VITE_API_URL ?? '',
      placeId,
      userId,
      token: session.token,
      report: reportReceiverEvent,
    })
  }, [scripted, session, placeId, userId])

  const source = scriptedSource ?? liveSource
  if (!source) return null
  // Demo only: hold the recovery screen still, to look at it.
  if (scripted === 'demo' && demoScene(window.location.search) === 'recover') return <ErrorFallback />

  return <Display placeId={placeId} source={source} />
}
